import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHatWizard } from '@fortawesome/free-solid-svg-icons';

const tripadvisorLogo = 'https://BelvoirCastle.b-cdn.net/Partners/tripadvisor.webp';
const hiddenEnglandLogo = 'https://BelvoirCastle.b-cdn.net/Partners/hidden-england.webp';
const rhsLogo = 'https://BelvoirCastle.b-cdn.net/Partners/rhs.webp';
const historicHousesLogo = 'https://BelvoirCastle.b-cdn.net/Partners/historic-houses.webp';
const visitLincolnshireLogo = 'https://BelvoirCastle.b-cdn.net/Partners/visit-lincolnshire.webp';
const visitNottinghamshireLogo = 'https://BelvoirCastle.b-cdn.net/Partners/visit-nottinghamshire.webp';
const southKestevenLogo = 'https://BelvoirCastle.b-cdn.net/Partners/south-kesteven.webp';
const explorers = 'https://BelvoirCastle.b-cdn.net/Partners/explorers.webp';
const visitEnglandBadge = 'https://BelvoirCastle.b-cdn.net/Partners/visitEnglandBadge.webp';


const whiteLogo = 'https://BelvoirCastle.b-cdn.net/Logos/whiteLogo.webp';

const sans =
  "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

const FooterWrapper = styled.footer`
  position: relative;
  background: linear-gradient(
    165deg,
    ${props => props.theme.colors.primary} 0%,
    #0a1430 52%,
    #070f24 100%
  );
  color: rgba(255, 255, 255, 0.92);
  padding: clamp(2.5rem, 6vw, 3.75rem) 0 0;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(
      90deg,
      transparent 0%,
      ${props => props.theme.colors.accent} 22%,
      ${props => props.theme.colors.accent} 78%,
      transparent 100%
    );
    opacity: 0.95;
  }

  max-width: 100%;
  overflow-x: clip;

  @media (max-width: 767px) {
    padding-bottom: 4.75rem;
  }
`;

const Inner = styled.div`
  width: 100%;
  max-width: min(1280px, 100%);
  margin: 0 auto;
  padding: 0 clamp(1.25rem, 4vw, 2rem);
  min-width: 0;
`;

const MainGrid = styled.div`
  display: grid;
  gap: clamp(2rem, 4vw, 2.75rem);
  grid-template-columns: minmax(0, 1fr);
  min-width: 0;

  @media (min-width: 900px) {
    grid-template-columns: minmax(0, 1.1fr) minmax(0, 1.85fr);
    align-items: start;
  }
`;

const BrandBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: clamp(1.25rem, 3vw, 1.75rem);
  min-width: 0;
`;

const LogoLink = styled(Link)`
  display: inline-block;
  align-self: flex-start;
  line-height: 0;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.88;
  }

  img {
    width: min(220px, 72vw);
    height: auto;
  }

  @media (max-width: 899px) {
    align-self: center;

    img {
      width: min(200px, 55vw);
    }
  }
`;

const Tagline = styled.p`
  font-family: ${sans};
  font-size: clamp(0.9rem, 1.8vw, 0.98rem);
  line-height: 1.55;
  color: rgba(255, 255, 255, 0.58);
  margin: 0;
  max-width: min(28rem, 100%);

  @media (max-width: 899px) {
    text-align: center;
    margin-left: auto;
    margin-right: auto;
  }
`;

const PartnersSection = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.85rem 1.15rem;
  align-items: center;
  min-width: 0;
  max-width: 100%;
  padding: 1rem 0 0.25rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);

  @media (max-width: 899px) {
    justify-content: center;
    padding-top: 1.25rem;
  }

  @media (max-width: 520px) {
    flex-wrap: nowrap;
    justify-content: flex-start;
    overflow-x: auto;
    overflow-y: hidden;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: thin;
    scrollbar-color: rgba(255, 255, 255, 0.2) transparent;
    padding-bottom: 0.65rem;
    margin-left: -0.25rem;
    margin-right: -0.25rem;
    padding-left: 0.25rem;
    padding-right: 0.25rem;

    &::-webkit-scrollbar {
      height: 4px;
    }
    &::-webkit-scrollbar-thumb {
      background: rgba(255, 255, 255, 0.22);
      border-radius: 4px;
    }
  }

  img {
    width: auto;
    height: 42px;
    object-fit: contain;
    opacity: 0.88;
    transition: opacity 0.2s ease;
    flex-shrink: 0;

    &:hover {
      opacity: 1;
    }
  }

  img[alt='Visit England'] {
    height: 50px;
  }

  @media (max-width: 768px) {
    img {
      height: 38px;
    }
    img[alt='Visit England'] {
      height: 46px;
    }
  }
`;

const LinkColumns = styled.div`
  display: grid;
  gap: clamp(1.75rem, 4vw, 2.25rem);
  grid-template-columns: minmax(0, 1fr);
  min-width: 0;

  @media (min-width: 520px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (min-width: 720px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
`;

const SectionTitle = styled.h3`
  font-family: ${sans};
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.48);
  margin: 0 0 1rem;
`;

const SectionHeadingDisplay = styled.span`
  display: block;
  font-family: ${props => props.theme.fonts.main};
  font-size: clamp(1.05rem, 2vw, 1.2rem);
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: none;
  color: #fff;
  margin-bottom: 0.65rem;

  &::after {
    content: '';
    display: block;
    width: 2rem;
    height: 2px;
    margin-top: 0.55rem;
    border-radius: 2px;
    background: ${props => props.theme.colors.accent};
    opacity: 0.9;
  }
`;

const AddressLine = styled.p`
  font-family: ${sans};
  font-size: clamp(0.88rem, 1.65vw, 0.95rem);
  line-height: 1.55;
  color: rgba(255, 255, 255, 0.72);
  margin: 0 0 0.35rem;
`;

const ContactLine = styled.p`
  font-family: ${sans};
  font-size: clamp(0.88rem, 1.65vw, 0.95rem);
  line-height: 1.55;
  color: rgba(255, 255, 255, 0.72);
  margin: 0 0 0.5rem;
`;

const InlineLink = styled.a`
  color: rgba(255, 255, 255, 0.92);
  text-decoration: none;
  overflow-wrap: anywhere;
  border-bottom: 1px solid rgba(157, 109, 73, 0.45);
  transition: color 0.2s ease, border-color 0.2s ease;

  &:hover {
    color: ${props => props.theme.colors.accent};
    border-color: ${props => props.theme.colors.accent};
  }
`;

const DirectionsLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-top: 0.85rem;
  padding: 0.72rem 1.35rem;
  border-radius: 999px;
  font-family: ${props => props.theme.fonts.main};
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  text-decoration: none;
  color: #fff;
  background: ${props => props.theme.colors.accent};
  border: 2px solid ${props => props.theme.colors.accent};
  transition: background 0.2s ease, border-color 0.2s ease, transform 0.2s ease,
    color 0.2s ease;

  &:hover {
    background: #fff;
    border-color: #fff;
    color: ${props => props.theme.colors.primary};
    transform: translateY(-1px);
  }

  &:focus-visible {
    outline: 2px solid ${props => props.theme.colors.accent};
    outline-offset: 3px;
  }
`;

const FooterLinks = styled.nav`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.45rem;
`;

const QuickLink = styled(Link)`
  font-family: ${sans};
  font-size: clamp(0.88rem, 1.65vw, 0.95rem);
  color: rgba(255, 255, 255, 0.82);
  text-decoration: none;
  padding: 0.2rem 0;
  transition: color 0.2s ease, transform 0.2s ease;

  &:hover {
    color: ${props => props.theme.colors.accent};
  }

  &:focus-visible {
    outline: 2px solid ${props => props.theme.colors.accent};
    outline-offset: 2px;
    border-radius: 2px;
  }
`;

const FooterBottom = styled.div`
  margin-top: clamp(2rem, 5vw, 2.75rem);
  padding: clamp(1.15rem, 3vw, 1.5rem) 0;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 1rem 1.5rem;
  font-family: ${sans};
  font-size: clamp(0.75rem, 1.5vw, 0.8125rem);
  color: rgba(255, 255, 255, 0.45);

  @media (max-width: 640px) {
    flex-direction: column;
    text-align: center;
  }
`;

const Copyright = styled.p`
  margin: 0;
  line-height: 1.5;
`;

const BottomLinks = styled.nav`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 0.5rem 0.35rem;
  row-gap: 0.65rem;
`;

const BottomLink = styled(Link)`
  color: rgba(255, 255, 255, 0.65);
  text-decoration: none;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  transition: color 0.2s ease;

  &:hover {
    color: ${props => props.theme.colors.accent};
  }

  &:focus-visible {
    outline: 2px solid ${props => props.theme.colors.accent};
    outline-offset: 2px;
  }
`;

const BottomSep = styled.span`
  color: rgba(255, 255, 255, 0.22);
  user-select: none;
  font-weight: 300;

  @media (max-width: 640px) {
    display: none;
  }
`;

const WitchEgg = styled(Link)`
  display: inline-block;
  font-size: 1rem;
  line-height: 1;
  opacity: 0.12;
  text-decoration: none;
  transition: opacity 0.3s ease;
  margin-left: 0.5rem;

  &:hover {
    opacity: 0.75;
  }
`;

const Footer = () => {
  const year = new Date().getFullYear();

  return (
    <FooterWrapper>
      <Inner>
        <MainGrid>
          <BrandBlock>
            <LogoLink to="/" aria-label="Belvoir Castle home">
              <img src={whiteLogo} alt="" />
            </LogoLink>
            <Tagline>
              A thousand years of history, gardens, farm shop and retail
              village — open for visits, tours and celebrations.
            </Tagline>
            <PartnersSection aria-label="Accreditations and partners">
              <img src={tripadvisorLogo} alt="TripAdvisor" />
              <img src={hiddenEnglandLogo} alt="Hidden England" />
              <img src={rhsLogo} alt="RHS" />
              <img src={historicHousesLogo} alt="Historic Houses" />
              <img src={visitLincolnshireLogo} alt="Visit Lincolnshire" />
              <img src={visitNottinghamshireLogo} alt="Visit Nottinghamshire" />
              <img src={southKestevenLogo} alt="Discover South Kesteven" />
              <img src={explorers} alt="Explorers" />
              <img src={visitEnglandBadge} alt="Visit England" />
            </PartnersSection>
          </BrandBlock>

          <LinkColumns>
            <section aria-labelledby="footer-visit">
              <SectionTitle id="footer-visit">Plan your visit</SectionTitle>
              <SectionHeadingDisplay as="h2">Visit us</SectionHeadingDisplay>
              <AddressLine>Belvoir Castle</AddressLine>
              <AddressLine>Grantham</AddressLine>
              <AddressLine>Leicestershire</AddressLine>
              <AddressLine>NG32 1PA</AddressLine>
              <DirectionsLink to="/your-visit/directions">
                Get directions
              </DirectionsLink>
            </section>

            <section aria-labelledby="footer-contact">
              <SectionTitle id="footer-contact">Get in touch</SectionTitle>
              <SectionHeadingDisplay as="h2">Contact</SectionHeadingDisplay>
              <ContactLine>
                Tel:{' '}
                <InlineLink href="tel:+441476871001">01476 871001</InlineLink>
              </ContactLine>
              <ContactLine>
                Email:{' '}
                <InlineLink href="mailto:info@belvoircastle.com">
                  info@belvoircastle.com
                </InlineLink>
              </ContactLine>
            </section>

            <section aria-labelledby="footer-links">
              <SectionTitle id="footer-links">Explore</SectionTitle>
              <SectionHeadingDisplay as="h2">Quick links</SectionHeadingDisplay>
              <FooterLinks>
                <QuickLink to="/your-visit/day-tickets">Buy tickets</QuickLink>
                <QuickLink to="/whats-on/events">Events</QuickLink>
                <QuickLink to="/weddings">Weddings</QuickLink>
                <QuickLink to="/castle-dining">Dining</QuickLink>
                <QuickLink to="/accommodations">Stay with us</QuickLink>
                <QuickLink to="/drone-policy">Drone policy</QuickLink>
              </FooterLinks>
            </section>
          </LinkColumns>
        </MainGrid>

        <FooterBottom>
          <Copyright>
            &copy; {year} Belvoir Castle. All rights reserved.
            <WitchEgg to="/halloween-belvoir-castle" aria-label="Enchanted Halloween Trail" tabIndex={-1}>
              <FontAwesomeIcon icon={faHatWizard} />
            </WitchEgg>
          </Copyright>
          <BottomLinks aria-label="Legal and practical links">
            <BottomLink to="/your-visit/opening-times">Opening times</BottomLink>
            <BottomSep aria-hidden>|</BottomSep>
            <BottomLink to="/your-visit/event-safety">Event safety</BottomLink>
            <BottomSep aria-hidden>|</BottomSep>
            <BottomLink to="/cookie-policy">Cookie policy</BottomLink>
            <BottomSep aria-hidden>|</BottomSep>
            <BottomLink to="/privacy-policy">Privacy policy</BottomLink>
            <BottomSep aria-hidden>|</BottomSep>
            <BottomLink to="/terms">Terms &amp; conditions</BottomLink>
          </BottomLinks>
        </FooterBottom>
      </Inner>
    </FooterWrapper>
  );
};

export default Footer;
